import { Injectable} from '@angular/core';
import { catchError, EMPTY, from, map, Observable, switchMap, tap} from 'rxjs';
import { MemberModel} from '../../../models/user.model';
import { ContractRoadmapModel, ContractWrapperModel } from '../../../models/contract.model';
import { MemberService } from '../../../services/member.service';
import { HelpersService } from '../../../services/helpers.service';
import { TokenService } from '../../../services/token.service';
import { MatDialog } from '@angular/material/dialog';
import { AppContractPartySelectComponent } from '../components/contract-party-select/contract-party-select.component';
import { ContractGetTargetDataService } from './contract-get-target-data.service';
import { ContractStatusToAddTragets, IContractDataModel} from '../models/contract-internal-models';
import { ContractDataService } from './contract-data.service';
import { ContractRestrictionService } from './contract-restrictions.service';
import { ContractAvatarsService } from './contract-avatar.service';
import { ContractAutoUpdateService } from './contract-autoupdate.service';
import { AlertService } from '../../../services/notify/alert.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class ContractLoadService {
  constructor(
    private memberService:MemberService,
    private helpersService:HelpersService,
    private tokenService:TokenService,
    private alertService:AlertService,
    private router:Router,
    private dialog:MatDialog,
    private contractDataService:ContractDataService,
    private contractGetTargetDataService:ContractGetTargetDataService,
    private contractRestrictionService:ContractRestrictionService,
    private contractAvatarsService:ContractAvatarsService,
    private contractAutoUpdateService:ContractAutoUpdateService,
  ) {
    this.contractAutoUpdateService.subReloadedIsNeeeded.asObservable()
    .subscribe(data=>this.onLoadingContract(data.contracGuid, undefined, false))
  }

  resetContractData():IContractDataModel {
    return {
      contract: null,
      contractRoadmap:null,
      contractRoadmapCopyBeforeEdit:null,
      currentProjectGuid:'',
      memberIsEmptyProject:null,
      membersInContract:[],
      allSignatoriesInContract:[],
      rights:{
        isCurrentMemberCanAddTargetsAfter:false,
        isContractBlockedForEdit:true,
        isFinanceBlockAvailable:false,
        isUserVerifiedContract:false,
        isCurrentMemberInContract:false,
        isCurrentMemberSigner:false,
        buttonsDisabled : {
          replenishment: true,
          barter: true,
          payment: true,
        },
      }
    }
  }

  onLoadingContract(contractGuid:string,isSave = false, verifyDomainOnLoad:boolean = true): void {
    let contractData = this.resetContractData()
    this.contractDataService.subContractStepToOpenOne.next({contractGuid:contractGuid,contractStep:null})
    this.contractDataService.subContractFormState.next('Submitting')
		from(this.memberService.getCurrentMember())
		.pipe(
			tap(profile=>this.contractDataService.currentMember = profile),
			switchMap(()=>this.contractDataService.getContractByGuid(contractGuid)),
			tap(data=>verifyDomainOnLoad? this.verifyDomain(data) : null),
			catchError(e => this.errorRequestHandler(e, true)))
    .subscribe(data => {
      this.updateContractData(data, isSave, contractData)
      if (this.contractDataService.currentMember) {
        this.contractDataService.currentMember.person = data.privates.find(el => el.member.guid === this.contractDataService.currentMember?.guid) ? true : false
      }
      this.handleMembersWithEmptyProjects(contractData);
      this.setCurrentProjectGuid(contractData)
      contractData.rights.isUserVerifiedContract = this.setVerificationStatus(contractData);
      this.setBaseRightsToContract(contractData)
      this.contractDataService.ContractDataCachedByGuid.set(contractGuid,contractData)
      this.contractDataService.bSubContractDataIsCached.next({guid:contractGuid})
      this.contractDataService.subContractFormState.next('Default')
      if (contractData.rights.isCurrentMemberSigner && ! contractData.memberIsEmptyProject)  {
        contractData.rights.isFinanceBlockAvailable = true
      }
      if (contractData.contractRoadmap?.steps?.length) {
        let domain = contractData?.contract?.contract.master_project.domain
        this.contractGetTargetDataService.getStepTargetData(contractData.contractRoadmap?.steps,this.contractDataService.currentMember?.guid!,contractGuid,domain)
        .subscribe(()=>{
          this.contractRestrictionService.prepareRestrictions(contractData)
          this.contractDataService.subAlltargetDataIsLoaded.next(true)
          this.contractAutoUpdateService.setAutoReloadInterval(contractData);
        })
      } else {
        this.contractDataService.subAlltargetDataIsLoaded.next(true)
        this.contractAutoUpdateService.setAutoReloadInterval(contractData);
      }
      this.memberService.getAllMembers().then(members => {
        this.getAllMembers(members,contractData)
      });
    });
  }
          
  errorRequestHandler(error: any, navigate = false): Observable<never> {
    this.contractDataService.subContractFormState.next('Default')
      navigate ? this.router.navigate(['account', 'contracts', 'all','all_statuses']) : null;
      this.alertService.setAlert(error.message, 'blue'); 
      return EMPTY
  }

  updateContractData(data: ContractWrapperModel, isSave: boolean, contractData: IContractDataModel) {
    contractData.contract = this.addPrivatesToProjectSection(data);
    contractData.contract.roadmap_active === null ? contractData.contract.roadmap_active = new ContractRoadmapModel([]) : null;
    contractData.contractRoadmap = contractData.contract.roadmap_active;
    contractData.contractRoadmapCopyBeforeEdit = structuredClone(contractData.contract.roadmap_active);
  }

  handleMembersWithEmptyProjects(contractData: IContractDataModel) {
    contractData.memberIsEmptyProject = contractData?.contract?.members_empty_project.find(ep => ep.member.guid === this.contractDataService.currentMember?.guid && ep.can_verify) || null;
    contractData.memberIsEmptyProject ? this.selectProjectFromList(contractData): null;
  }

  setVerificationStatus(contractData: IContractDataModel): boolean {
    return contractData?.contract?.projects.some(project => project.members.signatories.filter(signer => ((signer.member.guid === this.contractDataService.currentMember?.guid) && signer.verified_at)).length > 0) || false
  }
  
  setCurrentProjectGuid(contractData: IContractDataModel) {
    contractData.currentProjectGuid = contractData?.contract?.projects.find(el =>
        [...el.members.observers.map(el => el.member?.guid), el.members.signatories.map(el => el.member?.guid)].flat().includes(this.contractDataService.currentMember?.guid||'')
    )?.project?.guid || ''
    let blockActions = this.contractDataService.currentMember?.person === false && contractData?.contract?.contract?.master_project?.guid !== contractData.currentProjectGuid
    contractData.rights.buttonsDisabled = {
        replenishment: blockActions,
        barter: blockActions,
        payment: blockActions,
    }
  }

  addPrivatesToProjectSection(data: ContractWrapperModel): ContractWrapperModel {
    data.privates.forEach(person =>
      data.projects.filter(el => el.project.guid === person.member.guid)?.length ? null :
          data.projects.push({
            ...person,
            project: {
              created_at: '',
              disk_space: 'null',
              domain: '',
              guid: person.member.guid,
              name: this.helpersService.fullName(person.member)||'',
              type: 'person',
              subtype: 'MEMBER'
            },
            members: {
              signatories: [{
                member: person.member,
                can_verify: true,
                verified_at: person.verified_at,
              }],
              observers: []
            }
        }))
    return data
  }

  setBaseRightsToContract(contractData: IContractDataModel) {
    contractData.contract? contractData.membersInContract = structuredClone(contractData.contract?.members_empty_project) : null;
    
    contractData.allSignatoriesInContract = [];
    
    for (const project of contractData.contract?.projects||[]) {
        contractData.allSignatoriesInContract = contractData.allSignatoriesInContract.concat(project.members.signatories);
        contractData.membersInContract = contractData.membersInContract?.concat([...project.members.signatories, ...project.members.observers]);
    }
    
    contractData.rights.isCurrentMemberSigner = !!contractData.allSignatoriesInContract.find(x => x.member.guid === this.contractDataService.currentMember?.guid);
    contractData.rights.isCurrentMemberInContract = !!contractData.membersInContract?.find(x => x.member.guid === this.contractDataService.currentMember?.guid);
    
    contractData.rights.isContractBlockedForEdit = contractData?.contract?.contract.status !== 'open' || contractData.rights.isCurrentMemberInContract !== true;
    contractData.rights.isCurrentMemberCanAddTargetsAfter = 
      (ContractStatusToAddTragets.includes(contractData.contract?.contract.status||'closed') && contractData.rights.isCurrentMemberSigner === true)

    this.contractDataService.subContractBlockEdit.next(contractData.rights.isContractBlockedForEdit)
    
    let guids = contractData.membersInContract.map(el=>el.member.guid)
    this.contractAvatarsService.getAvatars(guids).subscribe()
    
    this.contractDataService.subUserPrivilegesSet.next(true)
  }

  getAllMembers (members: MemberModel[],contractData:IContractDataModel) {
    this.contractDataService.allMembers = members.filter(member => member.project_setting.observation_type !== 'blocked');
    contractData.membersInContract?.forEach(member => this.contractDataService.allMembers = this.contractDataService.allMembers.filter(x => x.email !== member.member.email))
  }

  verifyDomain(data:ContractWrapperModel) {
    let contractDomain = data.contract.master_project.domain;
    if (window.location.hostname!=='localhost' && window.location.hostname !== contractDomain) {
      let schema = window.location.href.split('://')[0];
      let url = schema + '://' + contractDomain + '/account/contract/' + data.contract.guid;
      let params = new URLSearchParams({
          auth_token: this.tokenService.getToken(),
      });
      document.location.href = url + '?' + params.toString();
    } 
  }

  selectProjectFromList(contractData:IContractDataModel) {
    let contractPartSelectRef = this.dialog.open(AppContractPartySelectComponent, {
      panelClass: 'project-select-padding-modalbox',
      height: '100%',
      maxHeight: '90vh',
      disableClose:true
    })
    contractPartSelectRef.componentInstance.contractGuid = contractData.contract?.contract?.guid.toString()||''
    contractPartSelectRef.componentInstance.currentMember = this.contractDataService.currentMember!
    contractPartSelectRef.componentInstance.memberIsEmptyProject = contractData.memberIsEmptyProject!
  }
}